/**
 * Game mode arena, Round 2 (plan 47 «Round 2 — the contract» § 1): the
 * translate-it challenge appears ONCE, as a highlighted block INSIDE the coach
 * bubble that served it (the pinned challenge card is gone).
 *
 * Two pure pieces:
 *
 * - **Which bubble served which challenge** ({@link advanceChallengeTags}):
 *   the `game.round` `challenge` event is sent when the coach's reply ENDS —
 *   while its bubble is usually still the LIVE one (the bubble commits when
 *   the audio ends), so the tag waits as `pending` and lands on the next coach
 *   bubble that commits. With no live bubble it goes to the newest coach
 *   bubble. Seeded history bubbles get theirs from the next learner turn's
 *   persisted round ({@link selectHistoricalChallengeTags}); the session
 *   detail from `turns[].gameRound` ({@link selectTranscriptChallengeTags}).
 * - **Where the block goes in the bubble's text** ({@link locateChallengeBlock}),
 *   the contract's locate rule: the span's offsets when they still match the
 *   raw reply, else the LAST occurrence of the span's text, else the
 *   normalized `challenge` found in the clean text (last occurrence), else a
 *   block under the text. The result is in CLEAN coordinates (the markdown
 *   markers stripped by `parseInlineMarkdown`) — the same string the karaoke
 *   and tap-a-word index — so the block's words keep their character offsets:
 *   the karaoke highlights them and a tap selects them like any other word.
 */
import {
  stripMarkdownForSpeech,
  type GameBand,
  type GameChallengeSpan,
  type GameRematch,
  type TranscriptGameSolution,
} from '@orborix/shared-types';
import type { GameRoundLive, VoiceMessage } from '@orborix/voice-client';
import type { TranscriptTurn } from '../../lib/sessionsApi.js';

/** The challenge a coach bubble served, as its block shows it. */
export interface GameChallengeTag {
  /** «Ronda N» (the server's count of distinct challenges in the session). */
  round: number;
  /** The Spanish sentence, normalized (no `**`, no wrapping quotes). */
  challenge: string;
  attemptNo: number;
  /** «Revancha» (`retry`) / «2º intento» (`second-shot`); `null` on a first try. */
  rematch: GameRematch | null;
  /** Where it sits in the raw reply (live only; `null` = locate by text). */
  span: GameChallengeSpan | null;
  band: GameBand | null;
}

/** The tag of a live `challenge` event, `null` when it is not one. */
export function challengeTagOf(round: GameRoundLive | null): GameChallengeTag | null {
  if (!round || round.event !== 'challenge' || round.seed !== 'translate-it' || !round.challenge) {
    return null;
  }
  return {
    round: round.round,
    challenge: round.challenge,
    attemptNo: round.attemptNo,
    rematch: round.rematch,
    span: round.challengeSpan,
    band: round.band,
  };
}

/* -------------------------------------------------------------------------- */
/* Locate                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Where the block goes, in CLEAN coordinates (`parseInlineMarkdown(text).clean`):
 *
 * - `inline` — `[spanStart, spanEnd)` is everything the challenge occupies,
 *   markers included (a pair of quotes stays in the clean text: `«…»`);
 *   `[start, end)` is the sentence itself. What lies between the two (the
 *   markers, the whitespace next to them) is never shown.
 * - `below` — not found in the text: the block shows the normalized
 *   `challenge` under the text (no karaoke on it).
 */
export type ChallengeBlockPlacement =
  | { kind: 'inline'; spanStart: number; start: number; end: number; spanEnd: number }
  | { kind: 'below' };

/** Characters trimmed off the edges of the block (wrapping quotes and blanks). */
const EDGE_NOISE = /[\s«»"“”]/u;

/** The raw range of the span: its offsets when they still match, else its LAST occurrence. */
function rawSpanRange(
  raw: string,
  span: GameChallengeSpan | null,
): readonly [number, number] | null {
  if (!span || span.text === '') return null;
  if (span.end <= raw.length && raw.slice(span.start, span.end) === span.text) {
    return [span.start, span.end];
  }
  const at = raw.lastIndexOf(span.text);
  return at === -1 ? null : [at, at + span.text.length];
}

/** A raw offset → its clean offset (the markdown strip is a per-character removal). */
function cleanOffset(raw: string, rawIndex: number): number {
  return stripMarkdownForSpeech(raw.slice(0, rawIndex)).length;
}

/** `[spanStart, spanEnd)` trimmed of quotes and blanks at both edges; `null` if nothing is left. */
function trimmed(
  clean: string,
  spanStart: number,
  spanEnd: number,
): Extract<ChallengeBlockPlacement, { kind: 'inline' }> | null {
  let start = spanStart;
  let end = spanEnd;
  while (start < end && EDGE_NOISE.test(clean[start]!)) start += 1;
  while (end > start && EDGE_NOISE.test(clean[end - 1]!)) end -= 1;
  return start < end ? { kind: 'inline', spanStart, start, end, spanEnd } : null;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

/**
 * The LAST occurrence of the normalized challenge in the clean text: exact
 * first, then the same words with blanks / emoji between them and any case
 * (the server normalizes those away). Widened to a pair of quotes that wraps
 * it, so they are hidden too.
 */
function locateNormalized(clean: string, challenge: string): readonly [number, number] | null {
  const words = challenge
    .trim()
    .split(/\s+/u)
    .filter((w) => w !== '');
  if (words.length === 0) return null;
  let found: readonly [number, number] | null = null;
  const exact = clean.lastIndexOf(challenge.trim());
  if (exact !== -1) found = [exact, exact + challenge.trim().length];
  else {
    const pattern = new RegExp(
      words.map(escapeRegExp).join('[\\s\\p{Extended_Pictographic}\\u{FE0F}\\u{200D}]+'),
      'giu',
    );
    for (const m of clean.matchAll(pattern)) found = [m.index, m.index + m[0].length];
  }
  if (!found) return null;
  let [s, e] = found;
  const open = clean[s - 1];
  const close = clean[e];
  if (
    (open === '«' && close === '»') ||
    (open === '“' && close === '”') ||
    (open === '"' && close === '"')
  ) {
    s -= 1;
    e += 1;
  }
  return [s, e];
}

/**
 * The contract's locate rule over a coach bubble's RAW text (markdown
 * included, the concatenation of the reply's token deltas — `VoiceMessage.text`).
 */
export function locateChallengeBlock(
  rawText: string,
  tag: Pick<GameChallengeTag, 'challenge' | 'span'>,
): ChallengeBlockPlacement {
  const clean = stripMarkdownForSpeech(rawText);
  const raw = rawSpanRange(rawText, tag.span);
  if (raw) {
    const placed = trimmed(clean, cleanOffset(rawText, raw[0]), cleanOffset(rawText, raw[1]));
    if (placed) return placed;
  }
  const normalized = locateNormalized(clean, tag.challenge);
  if (normalized) {
    const placed = trimmed(clean, normalized[0], normalized[1]);
    if (placed) return placed;
  }
  return { kind: 'below' };
}

/* -------------------------------------------------------------------------- */
/* Live: which coach bubble served which challenge                             */
/* -------------------------------------------------------------------------- */

export interface ChallengeTagsState {
  /** The `gameRound` already looked at (by identity). */
  seenRound: unknown;
  /** `messages.length` already looked at. */
  seenLen: number;
  /** A challenge served by the LIVE coach bubble, waiting for it to commit. */
  pending: GameChallengeTag | null;
  /** Coach bubble index → the challenge it served. */
  byIndex: ReadonlyMap<number, GameChallengeTag>;
}

export const INITIAL_CHALLENGE_TAGS: ChallengeTagsState = {
  seenRound: null,
  seenLen: 0,
  pending: null,
  byIndex: new Map(),
};

function newestLiveBot(messages: ReadonlyArray<VoiceMessage>, from: number, to: number): number {
  for (let i = to - 1; i >= from; i -= 1) {
    const m = messages[i];
    if (m && m.who === 'bot' && !m.historical) return i;
  }
  return -1;
}

function firstLiveBot(messages: ReadonlyArray<VoiceMessage>, from: number, to: number): number {
  for (let i = from; i < to; i += 1) {
    const m = messages[i];
    if (m && m.who === 'bot' && !m.historical) return i;
  }
  return -1;
}

/**
 * One step of the challenge tags, from what the screen sees now. Returns
 * `prev` itself when nothing changed (the hook stores it during render only
 * then).
 *
 * - A NEW `challenge` round (identity change) of the running game
 *   (`accept`): with a live coach bubble on screen it waits as `pending`,
 *   else it tags the newest live coach bubble.
 * - New messages (applied first): a `pending` tag lands on the first live
 *   coach bubble among them (the commit of the bubble that served it). A
 *   commit and its challenge seen in the same step meet through the newest
 *   coach bubble.
 * - The thread shrinking (a reseed) drops the tags past its new length.
 */
export function advanceChallengeTags(
  prev: ChallengeTagsState,
  now: {
    messages: ReadonlyArray<VoiceMessage>;
    gameRound: GameRoundLive | null;
    /** The round belongs to the running translate-it game. */
    accept: boolean;
    /** A live (streaming / still speaking) coach bubble is on screen. */
    liveCoach: boolean;
  },
): ChallengeTagsState {
  let next = prev;
  const len = now.messages.length;
  // New messages first: they can only be the commit of a tag already waiting
  // (a tag set in this same step belongs to a bubble still live, or is placed
  // on the newest coach bubble below).
  if (len !== prev.seenLen) {
    const byIndex = new Map(next.byIndex);
    let pending = next.pending;
    if (len < prev.seenLen) {
      for (const i of byIndex.keys()) if (i >= len) byIndex.delete(i);
    } else if (pending) {
      const at = firstLiveBot(now.messages, prev.seenLen, len);
      if (at !== -1) {
        byIndex.set(at, pending);
        pending = null;
      }
    }
    next = { ...next, seenLen: len, byIndex, pending };
  }
  if (now.gameRound !== prev.seenRound) {
    next = { ...next, seenRound: now.gameRound };
    const tag = now.accept ? challengeTagOf(now.gameRound) : null;
    if (tag) {
      if (now.liveCoach) next = { ...next, pending: tag };
      else {
        const at = newestLiveBot(now.messages, 0, len);
        if (at !== -1)
          next = { ...next, pending: null, byIndex: new Map(next.byIndex).set(at, tag) };
        else next = { ...next, pending: tag };
      }
    }
  }
  return next;
}

/**
 * The seeded history («Continuar»): each historical coach bubble that the
 * next learner bubble answered as a translate-it attempt served that
 * attempt's challenge (its persisted round). Located by text (no span).
 */
export function selectHistoricalChallengeTags(
  messages: ReadonlyArray<VoiceMessage>,
): ReadonlyMap<number, GameChallengeTag> {
  const out = new Map<number, GameChallengeTag>();
  let lastBot = -1;
  messages.forEach((m, i) => {
    if (!m.historical) return;
    if (m.who === 'bot') lastBot = i;
    if (m.who !== 'user') return;
    const a = m.gameAttempt;
    if (lastBot !== -1 && a?.challenge && a.seed === 'translate-it' && !out.has(lastBot)) {
      out.set(lastBot, {
        round: a.round,
        challenge: a.challenge,
        attemptNo: a.attemptNo,
        rematch: a.rematch,
        span: null,
        band: a.band,
      });
    }
    lastBot = -1;
  });
  return out;
}

/**
 * The session detail: turn index → the challenge its COACH reply served.
 * A turn's learner text answered the challenge served by the previous turn's
 * reply (`turns[].gameRound`); a «No sé» (`gameSolutions`, `source: 'reveal'`)
 * revealed the challenge served by the reply of turn `afterTurnIndex`.
 */
export function selectTranscriptChallengeTags(
  turns: ReadonlyArray<TranscriptTurn>,
  solutions: ReadonlyArray<TranscriptGameSolution> = [],
): ReadonlyMap<number, GameChallengeTag> {
  const out = new Map<number, GameChallengeTag>();
  let prevWithReply: TranscriptTurn | null = null;
  for (const t of turns) {
    const r = t.gameRound;
    if (
      r?.challenge &&
      r.seed === 'translate-it' &&
      prevWithReply &&
      !out.has(prevWithReply.turnIndex)
    ) {
      out.set(prevWithReply.turnIndex, {
        round: r.round,
        challenge: r.challenge,
        attemptNo: r.attemptNo,
        rematch: r.rematch,
        span: null,
        band: r.band,
      });
    }
    if (t.assistantText && t.assistantText.trim() !== '') prevWithReply = t;
  }
  for (const s of solutions) {
    if (s.source !== 'reveal' || s.afterTurnIndex === null || out.has(s.afterTurnIndex)) continue;
    const turn = turns.find((t) => t.turnIndex === s.afterTurnIndex);
    if (!turn?.assistantText) continue;
    out.set(s.afterTurnIndex, {
      round: s.round,
      challenge: s.challenge,
      attemptNo: 1,
      rematch: null,
      span: null,
      band: s.band,
    });
  }
  return out;
}
